import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EMPRESAS, getEntity } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { Badge, Card, ErrorBox, Kpi, PageHeader, Spinner, Table, brl, brlCompact, num } from "../components/ui.js";
import { Modal } from "../components/Modal.js";
import { FormEngine } from "../components/FormEngine.js";

const now = new Date("2026-06-01");
const STATUS = ["", "LIBERADO", "AGENDADO", "CARREGADO"];
const fmtDateTime = (v: unknown) => (v ? String(v).replace("T", " ").slice(0, 16) : "—");

// De onde veio a viagem. 'data' = criada pela importação da programação.
const ORIGEM_LABEL: Record<string, string> = {
  data: "Programação",
  portal: "Portal",
  planilha: "Planilha",
};

// A planilha gravava o texto "ADICIONAR MOTORISTA" no lugar de deixar vazio;
// para o operador isso é "ainda não vinculado".
const SEM_MOTORISTA = new Set(["", "—", "ADICIONAR MOTORISTA"]);

interface Change {
  id: number;
  action: string;
  actor_email: string | null;
  at: string;
  diff: Record<string, { de: unknown; para: unknown }> | null;
}

// Drawer de histórico de alterações (auditoria) de uma viagem.
function HistoryModal({ id, onClose }: { id: number; onClose: () => void }) {
  const hist = useQuery({ queryKey: ["op-history", id], queryFn: async () => (await api.get(`/operacoes/${id}/history`)).data as Change[] });
  return (
    <Modal title={`Histórico da viagem #${id}`} onClose={onClose}>
      {hist.isLoading ? <Spinner /> : (hist.data ?? []).length === 0 ? (
        <p className="text-sm text-slate-400">Sem alterações registradas para esta viagem.</p>
      ) : (
        <div className="space-y-3">
          {(hist.data ?? []).map((c) => (
            <div key={c.id} className="rounded-lg border border-slate-100 p-3 text-sm">
              <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                <span className="badge bg-slate-100 text-slate-600">{c.action}</span>
                <span>{c.actor_email ?? "—"}</span>
                <span>· {fmtDateTime(c.at)}</span>
              </div>
              {c.diff ? (
                <ul className="space-y-0.5">
                  {Object.entries(c.diff).map(([k, v]) => (
                    <li key={k} className="text-slate-600">
                      <span className="font-medium">{k}</span>: <span className="text-red-500 line-through">{String(v.de ?? "—")}</span> → <span className="text-green-600">{String(v.para ?? "—")}</span>
                    </li>
                  ))}
                </ul>
              ) : <span className="text-slate-400">Criação do registro.</span>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

export function Operacoes() {
  const { canWrite } = useAuth();
  const qc = useQueryClient();
  const [ano, setAno] = useState(now.getUTCFullYear());
  const [mes, setMes] = useState(now.getUTCMonth() + 1);
  const [novo, setNovo] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [history, setHistory] = useState<number | null>(null);
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("");
  // A tabela deixou de ser só VEGA (migration 018): USIMINAS e CL agora têm
  // camada tipada. Sem este filtro a grade misturaria as três operações.
  const [empresa, setEmpresa] = useState("");
  const writable = canWrite("viagem_vega");

  // Busca no SERVIDOR e em TODAS as competências. Antes a tela baixava só o
  // mês e filtrava no cliente: procurar um transporte de outro mês não achava
  // nada ("procuro o número do transporte e não consigo achar").
  //
  // Debounce de 300 ms: sem ele cada TECLA disparava uma consulta global, e as
  // primeiras teclas ("1", "15"…) casavam com quase tudo — a busca mais cara
  // possível, repetida a cada letra.
  const [buscaDebounced, setBuscaDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(busca.trim()), 300);
    return () => clearTimeout(t);
  }, [busca]);

  const buscaAtiva = buscaDebounced.length > 0;
  const digitando = busca.trim() !== buscaDebounced;
  const grid = useQuery({
    queryKey: ["operacoes", ano, mes, buscaDebounced, empresa],
    // Mantém a lista anterior enquanto a nova carrega: sem isso a grade e os
    // KPIs sumiam a cada tecla (o "pisca" que faz parecer que nada foi achado).
    placeholderData: (anterior) => anterior,
    queryFn: async () => {
      const qs = buscaDebounced
        ? `q=${encodeURIComponent(buscaDebounced)}`
        : `ano=${ano}&mes=${mes}`;
      const r = await api.get(`/operacoes?${qs}${empresa ? `&empresa=${empresa}` : ""}`);
      return {
        rows: r.data as Record<string, unknown>[],
        // Lista cortada precisa ser DITA — corte silencioso parece "não existe".
        truncado: r.headers["x-resultado-truncado"] === "true",
        limite: Number(r.headers["x-resultado-limite"] ?? 0),
      };
    },
  });

  const save = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      editing
        ? (await api.patch(`/entities/viagem_vega/${editing.id}`, data)).data
        : (await api.post("/entities/viagem_vega", data)).data,
    onSuccess: () => { setNovo(false); setEditing(null); qc.invalidateQueries({ queryKey: ["operacoes"] }); },
  });
  const del = useMutation({
    mutationFn: async (id: unknown) => (await api.delete(`/entities/viagem_vega/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operacoes"] }),
  });

  // A busca livre já veio filtrada do servidor; aqui resta só o status.
  const rows = useMemo(() => {
    const all = grid.data?.rows ?? [];
    if (!status) return all;
    return all.filter((r) => String(r.status_carga ?? "").toUpperCase() === status);
  }, [grid.data, status]);

  // KPIs da competência (sobre o conjunto filtrado).
  const kpis = useMemo(() => {
    const carregado = rows.filter((r) => String(r.status_carga).toUpperCase() === "CARREGADO").length;
    const atrasos = rows.filter((r) => r.atraso_coleta === "Atraso").length;
    const pbtc = rows.filter((r) => r.pbtc_flag === "PBTC").length;
    const frete = rows.reduce((a, r) => a + (Number(r.valor_total_frete) || 0), 0);
    return { total: rows.length, carregado, atrasos, pbtc, frete };
  }, [rows]);

  return (
    <div>
      <PageHeader
        title="Operações"
        subtitle="Lançamento, edição e acompanhamento das viagens por competência — VEGA, USIMINAS e CL"
        action={writable && <button className="btn-primary" onClick={() => { setEditing(null); setNovo(true); }}>+ Nova viagem</button>}
      />

      {/* KPIs: da competência OU do resultado da busca — o rótulo precisa
          dizer qual, senão o operador lê números da busca como sendo do mês. */}
      <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">
        {buscaAtiva
          ? `Resultado da busca “${buscaDebounced}”`
          : `Competência ${String(mes).padStart(2, "0")}/${ano}`}
      </p>
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi label="Viagens" value={num(kpis.total)} />
        <Kpi label="Carregadas" value={num(kpis.carregado)} />
        <Kpi label="Atrasos de coleta" value={num(kpis.atrasos)} />
        <Kpi label="Excesso PBTC" value={num(kpis.pbtc)} />
        <Kpi label="Frete total" value={brlCompact(kpis.frete)} hint={brl(kpis.frete)} />
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <select className="input w-24" value={mes} onChange={(e) => setMes(Number(e.target.value))} disabled={buscaAtiva}>
          {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, "0")}</option>)}
        </select>
        <select className="input w-24" value={ano} onChange={(e) => setAno(Number(e.target.value))} disabled={buscaAtiva}>
          {[2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="input w-40" data-testid="operacoes-empresa" value={empresa} onChange={(e) => setEmpresa(e.target.value)}>
          <option value="">Todas as empresas</option>
          {EMPRESAS.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className="input w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS.map((s) => <option key={s} value={s}>{s || "Todos os status"}</option>)}
        </select>
        <input
          className="input max-w-xs flex-1"
          data-testid="operacoes-search"
          placeholder="Buscar transporte, motorista, placa, cidade…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        {busca.trim().length > 0 && (
          <button className="btn-ghost text-xs" onClick={() => setBusca("")}>limpar busca</button>
        )}
        {digitando && <span className="self-center text-xs text-slate-400">buscando…</span>}
      </div>

      {buscaAtiva && (
        <p className="mb-3 text-xs text-slate-500" data-testid="operacoes-busca-global">
          Buscando em <b>todas as competências</b> — o filtro de mês fica suspenso enquanto há um termo de busca.
        </p>
      )}

      {grid.data?.truncado && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="operacoes-truncado">
          Mostrando as primeiras <b>{grid.data.limite}</b> viagens — há mais resultados.{" "}
          {buscaAtiva
            ? "Use um termo mais específico (o nº do transporte inteiro, por exemplo)."
            : "Filtre por status para reduzir a lista."}
        </p>
      )}

      {grid.isLoading ? <Spinner /> : grid.isError ? <ErrorBox error={grid.error} /> : (
        <>
          {del.isError && <div className="mb-3"><ErrorBox error={del.error} /></div>}
          <Card className="!p-0">
            <Table
              columns={[
                { key: "numero_ordem", label: "Ordem" },
                {
                  key: "empresa", label: "Empresa",
                  render: (v) => <span className="badge bg-brand-50 text-brand-700">{String(v ?? "—")}</span>,
                },
                {
                  key: "origem", label: "Origem",
                  render: (v) => <span className="badge bg-slate-100 text-slate-600">{ORIGEM_LABEL[String(v)] ?? String(v ?? "—")}</span>,
                },
                { key: "motorista", label: "Motorista", render: (v) => (SEM_MOTORISTA.has(String(v ?? "").toUpperCase()) ? <span className="text-amber-600">a vincular</span> : String(v ?? "—")) },
                { key: "cavalo", label: "Cavalo" },
                { key: "primeira_carreta", label: "1ª carreta" },
                { key: "dolly", label: "Dolly" },
                { key: "segunda_carreta", label: "2ª carreta" },
                { key: "cidade_entrega", label: "Cidade" },
                { key: "cliente_entrega", label: "Cliente" },
                { key: "status_carga", label: "Status", render: (v) => (v ? <Badge>{String(v).toUpperCase()}</Badge> : "—") },
                { key: "data_hora_coleta", label: "Coleta", render: fmtDateTime },
                { key: "hora_agendamento", label: "Hora ag.", render: (v) => (v ? String(v) : "—") },
                { key: "atraso_coleta", label: "Atraso", render: (v) => (v === "Atraso" ? <Badge>Atraso</Badge> : "—") },
                { key: "peso", label: "Peso (t)", render: (v) => num(v as number) },
                { key: "pbtc_flag", label: "PBTC", render: (v) => (v ? <Badge>{String(v)}</Badge> : "—") },
                { key: "valor_total_frete", label: "Frete", render: (v) => brl(v as number) },
                {
                  key: "_acoes", label: "", render: (_v, row) => (
                    <div className="flex gap-2 whitespace-nowrap">
                      <button className="text-xs text-slate-500 hover:underline" onClick={() => setHistory(Number(row.id))}>histórico</button>
                      {writable && <button className="text-xs text-brand-600 hover:underline" onClick={() => { setNovo(false); setEditing(row); }}>editar</button>}
                      {writable && <button className="text-xs text-red-500 hover:underline" onClick={() => { if (confirm("Excluir viagem?")) del.mutate(row.id); }}>excluir</button>}
                    </div>
                  ),
                },
              ]}
              rows={rows}
              stickyFirst
              maxHeightClass="max-h-[65vh]"
            />
          </Card>
          <p className="mt-3 text-xs text-slate-400">
            {buscaAtiva
              ? `${rows.length} viagem(ns) encontradas para “${buscaDebounced}” em todas as competências.`
              : `${rows.length} viagem(ns) na competência ${String(mes).padStart(2, "0")}/${ano}.`}
          </p>
        </>
      )}

      {(novo || editing) && (
        <Modal title={editing ? `Editar viagem #${editing.id}` : "Nova viagem"} onClose={() => { setNovo(false); setEditing(null); }}>
          {save.isError && <div className="mb-3"><ErrorBox error={save.error} /></div>}
          <FormEngine
            spec={getEntity("viagem_vega")!}
            initial={editing ?? {}}
            mode={editing ? "update" : "create"}
            onSubmit={(d) => save.mutate(d)}
            onCancel={() => { setNovo(false); setEditing(null); }}
            submitting={save.isPending}
          />
        </Modal>
      )}

      {history != null && <HistoryModal id={history} onClose={() => setHistory(null)} />}
    </div>
  );
}
